<script setup>
import { checkRequestExportInventory, exportInventory, getInventoryList } from '@/api/inventory'
import { i18n } from '@/i18n'
import { useStore } from '@/store'
import {
  templateRef,
  useAsyncState,
  useCurrentElement,
  useDebounceFn,
} from '@vueuse/core'
import {
  computed,
  getCurrentInstance,
  inject,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import InventoryList from '@/components/Inventory/InventoryList/index.vue'
import ModalExportInventory from './ModalExportInventory.vue'
import InventoryTour from '@/components/Inventory/InventoryList/InventoryTour.vue'

const router = useRouter()
const route = useRoute()

const { $notification } = getCurrentInstance().proxy

onMounted(() => {})
const store = useStore()
const restriction = computed(() => store.state.user.restriction_base)
const permission = computed(() => store.getters['user/can'](route.meta?.key ?? 'list-inventoryv2'))
const canEditReserved = computed(() => {
  const perms = permission.value
  if (Array.isArray(perms)) return perms.includes('WRITE')
  if (typeof perms === 'string') return perms.includes('WRITE')
  return false
})
const userBusinessList = computed(() => store.state.user.businessList || [])
const businessId = computed(() =>
  restriction.value === 'Warehouse'
    ? Object.keys(store.getters['user/businessListObjectByKey'])[0]
    : route.query.business_id,
)

const tabs = computed(() => [
  { title: i18n.t('utils.all'), key: 'ALL' },
  { title: i18n.t('productAvailability.IN_STOCK'), key: 'IN_STOCK' },
  {
    title: i18n.t('productAvailability.OUT_OF_STOCK'),
    key: 'OUT_OF_STOCK',
  },
])

const {
  state: warehouseList,
  isLoading: isLoadingWarehouse,
  execute: _fetchWarehouse,
} = useAsyncState(
  async () => {
    const { data } = await store.dispatch('warehouse/GETSALESWAREHOUSE', {
      business_id:
        businessId.value ??
        userBusinessList.value.map(business => business.business_id).join(','),
      size: 999999,
    })

    return [...(data || [])].sort((a, b) => a.name.localeCompare(b.name))
  },
  [],
  { immediate: false },
)
const warehouseListOptions = computed(() => {
  const list = warehouseList.value.map(warehouse => ({
    value: warehouse.id,
    label: warehouse.name,
  }))

  return [{ value: undefined, label: i18n.t('topBar.all_Warehouse') }, ...list]
})

const fetchWarehouseList = useDebounceFn(_fetchWarehouse, { wait: 500 })
watch(() => businessId.value, fetchWarehouseList, { immediate: true })

const page = computed(() => Number(route.query.page) || 1)
const pageSize = computed(() => Number(route.query.size) || 50)
const searchText = ref(route.query.search || '')

const inventoryListElement = templateRef('inventoryList')

const DEFAULT_INVENTORY_STATE = {
  data: [],
  totalRow: 0,
  statusCount: {
    ALL: 0,
    IN_STOCK: 0,
    OUT_OF_STOCK: 0,
    MINUS_OF_STOCK: 0,
  },
}

async function fetchInventory() {
  const params = {
    ...route.query,
    sort: 'title,asc',
    business_id: businessId.value,
    page: page.value,
    size: pageSize.value,
  }

  const state = { ...DEFAULT_INVENTORY_STATE }

  const { data } = await getInventoryList(params)

  state.data = data.content
  state.totalRow = data.total_of_elements
  state.statusCount = data.summary

  return state
}

const {
  state: inventory,
  isLoading,
  isReady,
  error,
  execute: executeFetchInventory,
} = useAsyncState(
  fetchInventory,
  { ...DEFAULT_INVENTORY_STATE },
  {
    immediate: false,
    shallow: true,
    resetOnExecute: false,
    onSuccess: () => {
      inventoryListElement.value?.scrollToTop()
    },
    onError: err => {
      $notification.error({
        message: i18n.t('utils.wrong'),
        description: `${i18n.t('productAvailability.error_fetch_inventory')}: ${err.message}`,
      })
    },
  },
)

watch(
  () => route.query,
  () => {
    executeFetchInventory()
  },
  { immediate: true },
)

function getInventoryStatusCount(key) {
  return inventory.value?.statusCount?.[key] || 0
}

function changeQuery(query = {}) {
  const newQuery = {
    ...route.query,
    ...query,
  }

  // Normalize and remove undefined
  Object.keys(newQuery).forEach(key => {
    if (newQuery[key] === undefined || newQuery[key] === null) {
      delete newQuery[key]
    } else {
      newQuery[key] = String(newQuery[key])
    }
  })

  // Compare with current query
  const currentQuery = route.query
  const isSame = Object.keys(newQuery).length === Object.keys(currentQuery).length &&
    Object.keys(newQuery).every(key => newQuery[key] === String(currentQuery[key]))

  if (isSame) return

  router.push({ query: newQuery }).catch(() => {})
}

function filterOption(input, option) {
  return option.componentOptions.children[0].text
    .toLowerCase()
    .includes(input.toLowerCase())
}

/**
 * Export inventory
 */

const loadingExport = ref(false)
const showModalExportExcel = ref(false)

async function exportInventoryList(val) {
  loadingExport.value = true
  const { business_id, warehouse_id, status } = val.form

  let warehouseList = []
  if (warehouse_id.includes('all')) {
    val.warehouses?.forEach(warehouse => {
      if (warehouse.value !== 'all') {
        warehouseList.push(warehouse.value)
      }
    })
  }

  try {
    const { data } = await exportInventory({
      business_id: business_id.includes('all')
        ? userBusinessList.value.map(business => business.business_id).join()
        : business_id.join(),
      warehouse_id: warehouse_id.includes('all')
        ? warehouseList.join()
        : warehouse_id.join(),
      status,
      lang: i18n.locale.split('-')[0],
    })

    onResponseExportInventory(data)
  } catch (err) {
    onErrorExportInventory(err)
  }
}

/**
 * Bulk Edit Reserved Stock
 */
function goToBulkEdit() {
  router.push({
    name: 'inventory.list.inventory.v2.import',
    query: route.query,
  })
}

const getProcessExportInventory = useDebounceFn(async function (request_id) {
  try {
    const { data } = await checkRequestExportInventory({
      request_id,
      params: {
        process_name: 'EXPORT_INVENTORY',
      },
    })

    onResponseExportInventory(data)
  } catch (err) {
    onErrorExportInventory(err)
  }
}, 200)

function onResponseExportInventory(data) {
  if (data?.status === 'FINISHED') {
    loadingExport.value = false
    showModalExportExcel.value = false
    if (data?.url) {
      window.location.href = data.url
    }

    return;
  }

  if (data?.status === 'FAILED') {
    return onErrorExportInventory()
  }
  
  return getProcessExportInventory(data.request_id)
}

function onErrorExportInventory(err = null) {
  loadingExport.value = false
  const message = err?.message || err?.response?.data?.message
  $notification.error({
    message: i18n.t('productAvailability.export_failed'),
    description: `${i18n.t('productAvailability.export_failed_desc')}${message ? `: ${message}` : ''}`,
  })
}
</script>

<template>
  <div class="page__inventory-list">
    <h1 class="h3 mb-4">
      {{ $t('productAvailability.productAvailability') }}
    </h1>
    <a-row :gutter="16" type="flex">
      <a-col v-if="restriction === 'Business'" :span="6">
        <a-select
          v-if="restriction === 'Business'"
          :value="$route.query.warehouse_id"
          allow-clear
          show-search
          size="large"
          class="w-100 select-antd-default"
          option-filter-prop="children"
          :filter-option="filterOption"
          :placeholder="$t('topBar.all_Warehouse')"
          :options="warehouseListOptions"
          :disabled="isLoading"
          @change="changeQuery({ warehouse_id: $event, page: 1 })"
        />
      </a-col>
      <a-col :span="8">
        <a-input-search
          v-model="searchText"
          allow-clear
          size="large"
          class="w-100"
          :enter-button="$t('utils.search')"
          :placeholder="`${$t('productAvailability.productName')} / SKU`"
          @search="changeQuery({ search: $event || undefined, page: 1 })"
        />
      </a-col>

      <a-col class="ml-auto d-flex" style="gap: 8px">
        <a-button
          id="tour-bulk-reserved"
          size="large"
          class="btn-bulk-edit"
          @click.prevent="goToBulkEdit"
        >
          <a-icon type="upload" />
          {{ $t('reservedStock.bulk_edit_reserved_stock') }}
        </a-button>
        <a-button
          type="primary"
          size="large"
          @click.prevent="showModalExportExcel = true"
        >
          <a-icon type="download" />
          {{ $t('warehouse.export_data') }}
        </a-button>
      </a-col>
    </a-row>

    <a-result
      v-if="error"
      class="py-5"
      :title="$t('utils.wrong')"
      :sub-title="error.message"
    >
      <template #icon>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="150"
          height="150"
          viewBox="0 0 24 24"
          class="text-muted"
          style="margin-bottom: -1rem"
        >
          <path
            fill="currentColor"
            d="M7.5 16.5H9V15h6v1.5h1.5V15H15v-1.5H9V15H7.5v1.5ZM8 12h1.5V9.5H8V12Zm6.5 0H16V9.5h-1.5V12ZM4 20q-.825 0-1.413-.588T2 18V6q0-.825.588-1.413T4 4h5.175q.4 0 .763.15t.637.425L12 6h8q.825 0 1.413.588T22 8v10q0 .825-.588 1.413T20 20H4Zm0-2h16V8H4v10Zm0 0V8v10Z"
          />
        </svg>
      </template>

      <template #extra>
        <a-button
          type="primary"
          :loading="isLoading"
          :disabled="isLoading"
          @click="executeFetchInventory"
        >
          {{ $t('topBar.refresh') || 'Refresh' }}
        </a-button>
      </template>
    </a-result>

    <div v-else>
      <a-tabs
        :active-key="$route.query.status || 'ALL'"
        size="large"
        class="pca-tab-store mt-4"
        @change="changeQuery({ status: $event, page: 1 })"
      >
        <a-tab-pane
          v-for="tab in tabs"
          :key="tab.key"
          :tab="`${tab.title} (${getInventoryStatusCount(tab.key)})`"
          :disabled="isLoading"
        />
      </a-tabs>

      <InventoryList
        ref="inventoryList"
        :data="inventory?.data || []"
        :loading="isLoading"
        :warehouse-list="warehouseList"
      />

      <div class="mt-4 text-right">
        <a-pagination
          :current="page"
          :page-size="pageSize"
          :page-size-options="['50']"
          :total="inventory?.totalRow"
          :show-total="
            (total, range) =>
              $t('utils.pagination_show_total', {
                perpage: `${range[0]} - ${range[1]}`,
                total,
              })
          "
          :locale="{ items_per_page: $t('utils.items_per_page') }"
          :disabled="isLoading"
          show-size-changer
          @change="changeQuery({ page: $event })"
          @showSizeChange="changeQuery({ size: $event })"
        />
      </div>
    </div>

    <InventoryTour />

    <ModalExportInventory
      :visible="showModalExportExcel"
      :loading="loadingExport"
      :status="tabs"
      :on-close="() => (showModalExportExcel = false)"
      @handleSubmitModal="exportInventoryList"
    />
  </div>
</template>

<style lang="scss">
.pca-tab-store {
  .ant-tabs-bar {
    background-color: #fff;
    margin-bottom: 0;
    border-bottom-color: transparent;
  }
  .ant-tabs-nav .ant-tabs-tab:hover,
  .ant-tabs-nav .ant-tabs-tab-active {
    color: #ff0a54;
  }
  .ant-tabs-ink-bar {
    background-color: #ff0a54;
  }
}
.select-antd-default.ant-select-lg .ant-select-selection__rendered {
  line-height: 38px !important;
}
</style>

<style scoped lang="scss">
.pca-tab-store::v-deep {
  .ant-tabs-large-bar .ant-tabs-nav-container {
    font-size: 14px;
  }

  .ant-tabs-large-bar .ant-tabs-tab {
    padding: 12px 14px;
  }
}

.btn-bulk-edit {
  border-color: #1890ff;
  color: #1890ff;
  display: flex;
  align-items: center;

  i {
    color: #1890ff;
  }

  &:hover,
  &:focus {
    border-color: #40a9ff;
    color: #40a9ff;
    background-color: #e6f7ff;

    i {
      color: #40a9ff;
    }
  }
}
</style>
